/* eslint-disable complexity */
import React, { useState, useEffect } from "react";
import withPreprocessedContent from "@lmig/dotcom-aspect-comparion-components/Utility/withPreprocessedContent";
import Head from "@lmig/dotcom-aspect-comparion-components/Head";
import Layout from "@lmig/dotcom-aspect-comparion-components/Layout";
import AgentListCount from "@lmig/dotcom-aspect-comparion-components/AgentListCount";
import FindAgentSearchBar from "@lmig/dotcom-aspect-comparion-components/Search/FindAgentSearchBar";
import { emsGraphQlQuery } from "@lmig/dotcom-aspect-comparion-helpers/emsGraphQlQuery";
import { getServerSideProps as callGetServerSideProps } from "@lmig/dotcom-aspect-comparion-helpers/staticPageController";
import fetchRetry from "@lmig/dotcom-aspect-comparion-helpers/fetchRetry";
import FindAnAgentResults from "@lmig/dotcom-aspect-comparion-components/Search/FindAnAgentResults";
import getConfig from 'next/config';
import Logger from '@lmig/dotcom-utilities-helpers/logger';

const logger = new Logger('client:layout:find-an-agent');

const FindAnAgent = withPreprocessedContent((props) => {
  const [isLocationSearch, setIsLocationSearch] = useState(true);
  const [expiredEntity, setExpiredEntity] = useState(undefined);

  useEffect(() => {
    if (props?.queryVals?.location === "false") {
      setIsLocationSearch(false);
    }

    if (props?.queryVals?.notification) {
      if (props?.queryVals?.notification === "expired-office-search") {
        setExpiredEntity("office");
      } else if (props?.queryVals?.notification === "expired-agent-search") {
        setExpiredEntity("agent");
      }
    }

  }, [props?.queryVals?.location, props?.queryVals?.notification]);

  return (
    <>
      <Head {...props.content.meta} />
      <Layout agentSession={false}>
        <FindAgentSearchBar isLocationSearch={isLocationSearch} searchVal={props?.queryVals?.query} radius={props.radius} expiredSearch={expiredEntity} />
        <div className="content" style={{ minHeight: '20rem' }}>
          {(props?.queryVals?.query === undefined) && <AgentListCount entityData={props?.agentsPerState} pageName="insurance-agent" />}
          {props?.queryVals?.query?.length > 0 &&
            <FindAnAgentResults
              agents={props?.agentResults}
              offices={props?.officeResults}
              bothAgentOffice={isLocationSearch}
              query={props?.queryVals?.query}
            />}
        </div>
      </Layout>
    </>
  );
});

const getServerSideProps = async (context) => {
  const resultProps = await callGetServerSideProps(context);
  resultProps.props.radius = context.query.radius ? Number(context.query.radius) : 25;
  const {
    serverRuntimeConfig: { baseUrl },
    publicRuntimeConfig: { regex: {
      address: addressRegexString,
      name: nameRegexString,
      zip: zipRegexString,
    }
    },
  } = getConfig();

  const addressRegex = new RegExp(addressRegexString);
  const nameRegex = new RegExp(nameRegexString);
  const zipRegex = new RegExp(zipRegexString);

  const { data: { stateNames } } = await emsGraphQlQuery({
    cacheKeyPrefix: 'findAnAgent-stateNames',
    params: {
      query: `query { 
        stateNames:StateMany{name}
    }` }
  });

  const agentCountPerState = {};

  await Promise.all(stateNames.map(async ({ name }) => {
    const stateQuery = `query{
      agentsPerStateOffice:AgentMany(limit:500,filter:{active:true,type:FIELD,offices:{address:{state:{name:"${name}"}}}}){alias}
      agentsPerStateLocalOffice:AgentMany(limit:500,filter:{active:true,type:FIELD,localOffice:{state:{name:"${name}"}}}){alias}
    }`;
    await emsGraphQlQuery({
      cacheKeyPrefix: `findAnAgent-agents-${name.replaceAll(" ", "_")}`,
      params: { query: stateQuery }
    }).then(data => {
      const local = data.data.agentsPerStateLocalOffice;
      const nonLocal = data.data.agentsPerStateOffice;

      // removes any duplicates from the count
      local.forEach(({ alias }) => {
        if (nonLocal.find(agent => agent.alias === alias)) {
          const id = nonLocal.map(agent => agent.alias).indexOf(alias);
          nonLocal.splice(id, 1);
        }
      });

      agentCountPerState[name.replaceAll(" ", "_")] = local.length + nonLocal.length;
    });
  }));

  let urls = [];
  // LM's fetch defaults to http for localhost
  const explicitBaseUrl = baseUrl === undefined ? '' : baseUrl;

  if (context?.query?.query?.length > 0) {
    if (context?.query?.location === "true") {
      if (zipRegex.test(context?.query?.query)) {
        urls = [`${explicitBaseUrl}/api/v1/agentSearch?zipcode=${context?.query?.query}&searchRadius=${context?.query?.radius}`, `${explicitBaseUrl}/api/v1/officeSearch?zipcode=${context?.query?.query}&searchRadius=${context?.query?.radius}`];
      } else if (addressRegex.test(context?.query?.query)) {
        urls = [`${explicitBaseUrl}/api/v1/agentSearch?address=${context?.query?.query}&searchRadius=${context?.query?.radius}`, `${explicitBaseUrl}/api/v1/officeSearch?address=${context?.query?.query}&searchRadius=${context?.query?.radius}`];
      } else {
        logger.warn(`Invalid regex for location, removing and redirecting to search`);
        context.res.redirect(302, "/insurance-agent");
        context.res.end();
      }
    } else if (nameRegex.test(context?.query?.query)) {
      try {
        const response = await fetchRetry(`${explicitBaseUrl}/api/v1/search?searchAgents=${context?.query?.query}`);
        const { searchResults } = await response.json();
        resultProps.props.agentResults = searchResults.agents;
      }
      catch (e) {
        return { props: { errors: [e.message], queryVals: context?.query } };
      }
    } else {
      logger.warn(`Invalid regex for name, removing and redirecting to search`);
      context.res.redirect(302, "/insurance-agent?location=false");
      context.res.end();
    }

    try {
      if (urls.length > 0) {
        const [agents, offices] = await Promise.all(urls.map(async (url) => {
          const urlResponse = await fetchRetry(url);

          return urlResponse.json();
        }));
        resultProps.props.agentResults = agents.data;
        resultProps.props.officeResults = offices.data;
        resultProps.props.radius = Math.max(agents.radius || 0, offices.radius || 0);
      }
    } catch (e) {
      return { props: { errors: [e.message], queryVals: context?.query } };
    }
  }

  resultProps.props.queryVals = context?.query;
  resultProps.props.agentsPerState = agentCountPerState;

  return resultProps;
};

export { getServerSideProps };

export default FindAnAgent;